<template>
  <v-main>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>

      <div class="st-root">
        <!-- Page Header -->
        <div class="st-page-header">
          <div class="d-flex align-center">
            <div class="st-header-icon-wrap mr-3">
              <v-icon color="white" size="24">mdi-file-code-outline</v-icon>
            </div>
            <div>
              <div class="white--text text-h6 font-weight-bold">SAC Template Editor</div>
              <div class="white--text text-caption" style="opacity: 0.75;">
                Design, save and manage reusable CAE JSON templates
              </div>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 10px;">
            <v-btn outlined small color="white" class="st-btn" @click="openLoadDialog">
              <v-icon left small>mdi-folder-open-outline</v-icon>
              Manage Templates
            </v-btn>
            <v-btn
              color="white"
              small
              :disabled="!isJsonValid"
              class="st-btn st-save-btn"
              elevation="0"
              @click="openSaveDialog"
            >
              <v-icon left small>mdi-content-save-outline</v-icon>
              Save Template
            </v-btn>
          </div>
        </div>

        <!-- Main Layout -->
        <div class="st-layout">
          <!-- Left Panel: JSON Editor -->
          <div class="st-left-panel">
            <div class="st-panel-header">
              <v-icon color="white" size="16" class="mr-2">mdi-code-braces</v-icon>
              <span class="white--text text-caption font-weight-bold">JSON Editor</span>
              <v-spacer />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small color="white" v-bind="attrs" v-on="on" @click="loadExample">
                    <v-icon size="16">mdi-code-tags</v-icon>
                  </v-btn>
                </template>
                <span>Load example template</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small color="white" v-bind="attrs" v-on="on" @click="formatJson">
                    <v-icon size="16">mdi-auto-fix</v-icon>
                  </v-btn>
                </template>
                <span>Format JSON</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small color="white" v-bind="attrs" v-on="on" @click="clearJson">
                    <v-icon size="16">mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>Clear</span>
              </v-tooltip>
            </div>

            <!-- Current template badge -->
            <div v-if="editingTemplate" class="st-editing-banner">
              <v-icon size="15" color="#1976d2" class="mr-1">mdi-pencil-circle-outline</v-icon>
              <span class="text-caption"
                >Editing: <strong>{{ editingTemplate.name }}</strong></span
              >
              <v-spacer />
              <v-btn x-small text color="grey" @click="clearEditing">New Template</v-btn>
            </div>

            <!-- Validation banners -->
            <div v-if="jsonInput && !isJsonValid" class="st-error-banner">
              <v-icon color="error" size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span class="text-caption">Invalid JSON</span>
            </div>
            <div v-if="jsonInput && isJsonValid" class="st-valid-banner">
              <v-icon color="success" size="16" class="mr-1">mdi-check-circle-outline</v-icon>
              <span class="text-caption">Valid JSON — ready to save</span>
            </div>

            <textarea
              v-model="jsonInput"
              class="st-editor"
              placeholder="Paste or type your CAE template JSON here..."
              @input="onEditorInput"
              spellcheck="false"
            ></textarea>

            <div class="st-action-bar">
              <v-btn outlined small color="grey darken-1" @click="$router.go(-1)" class="st-btn">
                <v-icon left small>mdi-arrow-left</v-icon> Back
              </v-btn>
            </div>
          </div>

          <!-- Right Panel: JSONCrack Viewer -->
          <div class="st-right-panel">
            <div class="st-panel-header">
              <v-icon color="white" size="16" class="mr-2">mdi-graph</v-icon>
              <span class="white--text text-caption font-weight-bold">Visual Graph</span>
              <v-spacer />
              <v-chip x-small color="rgba(255,255,255,0.2)" text-color="white">JSONCrack</v-chip>
            </div>
            <div class="st-iframe-wrap">
              <iframe
                id="stJsoncrackEmbed"
                src="http://localhost:8888/widget"
                width="100%"
                height="100%"
                class="st-iframe"
                @load="sendToEmbed"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Save Dialog ── -->
      <v-dialog v-model="saveDialog" max-width="480" persistent>
        <v-card class="st-dialog-card">
          <div class="st-dialog-header">
            <v-icon color="white" class="mr-2">mdi-content-save-outline</v-icon>
            <span>{{ editingTemplate ? 'Update Template' : 'Save New Template' }}</span>
          </div>
          <v-card-text class="pt-5 pb-2">
            <v-text-field
              v-model="saveForm.name"
              label="Template Name *"
              outlined
              dense
              :rules="[v => !!v || 'Name is required']"
              hide-details="auto"
              class="mb-4"
            />
            <v-textarea
              v-model="saveForm.description"
              label="Description (optional)"
              outlined
              dense
              rows="3"
              hide-details
            />
          </v-card-text>
          <v-card-actions class="px-6 pb-5">
            <v-spacer />
            <v-btn text @click="saveDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              :disabled="!saveForm.name || !saveForm.name.trim()"
              :loading="saving"
              elevation="0"
              @click="confirmSave"
            >
              {{ editingTemplate ? 'Update' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ── Manage Templates Dialog ── -->
      <v-dialog v-model="loadDialog" max-width="600">
        <v-card class="st-dialog-card">
          <div class="st-dialog-header">
            <v-icon color="white" class="mr-2">mdi-folder-open-outline</v-icon>
            <span>Saved Templates</span>
            <v-spacer />
            <v-btn icon small color="white" @click="loadDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-card-text class="pa-4">
            <div v-if="loadingTemplates" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="templates.length === 0" class="text-center py-8 grey--text">
              <v-icon size="48" color="grey lighten-2">mdi-file-outline</v-icon>
              <div class="mt-2 text-caption">No templates saved yet</div>
            </div>
            <div v-else class="st-template-list">
              <div v-for="tmpl in templates" :key="tmpl.id" class="st-template-row">
                <div class="st-template-info">
                  <div class="st-template-name">{{ tmpl.name }}</div>
                  <div v-if="tmpl.description" class="st-template-desc">{{ tmpl.description }}</div>
                  <div class="st-template-meta">
                    <v-icon size="21" class="mr-1">mdi-account-outline</v-icon>{{ tmpl.createdBy }}
                    &nbsp;·&nbsp;
                    <v-icon size="21" class="mr-1">mdi-calendar-outline</v-icon
                    >{{ formatDate(tmpl.createdAt) }}
                  </div>
                </div>
                <div class="d-flex" style="gap: 6px;">
                  <v-btn small depressed color="primary" @click="loadTemplateContent(tmpl)">
                    <v-icon left small>mdi-download-outline</v-icon>Load
                  </v-btn>
                  <v-btn
                    small
                    depressed
                    color="error"
                    outlined
                    @click="confirmDeleteTemplate(tmpl)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- ── Delete Confirm Dialog ── -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">Delete Template?</v-card-title>
          <v-card-text>
            This will permanently delete
            <strong>{{ templateToDelete && templateToDelete.name }}</strong
            >.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :loading="deleting" elevation="0" @click="executeDelete"
              >Delete</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import sacTemplateApi from '../api/sacTemplateApi';

const EXAMPLE_TEMPLATE = {
  claims: [
    {
      id: 'CLM-001',
      title: 'Secure by design',
      description: 'The product implements security-by-design principles throughout its lifecycle.',
      status: 'Draft',
      subClaims: [
        {
          id: 'SUB-001',
          title: 'No known exploitable vulnerabilities',
          description: 'The product shall be delivered without known exploitable vulnerabilities.',
          status: 'Draft',
          evidences: [
            {
              id: '1',
              title: 'SBOM Analysis Report',
              type: 'Document',
              status: 'Approved',
              description: 'Software Bill of Materials confirming no CVEs above CVSS 7.0.',
            },
          ],
        },
      ],
    },
  ],
};

export default {
  name: 'SacTemplateCreation',
  components: { Breadcrumbs },
  data() {
    return {
      jsonInput: '',
      isJsonValid: false,
      syncTimeout: null,

      // Editing state
      editingTemplate: null,

      // Dialogs
      saveDialog: false,
      loadDialog: false,
      deleteDialog: false,

      saveForm: { name: '', description: '' },
      saving: false,

      templates: [],
      loadingTemplates: false,

      templateToDelete: null,
      deleting: false,
    };
  },
  methods: {
    validateJson(val) {
      if (!val || !val.trim()) {
        this.isJsonValid = false;
        return;
      }
      try {
        JSON.parse(val);
        this.isJsonValid = true;
      } catch {
        this.isJsonValid = false;
      }
    },

    onEditorInput() {
      this.validateJson(this.jsonInput);
      clearTimeout(this.syncTimeout);
      this.syncTimeout = setTimeout(() => this.sendToEmbed(), 400);
    },

    sendToEmbed() {
      const iframe = document.getElementById('stJsoncrackEmbed');
      if (!iframe || !iframe.contentWindow) {
        setTimeout(() => this.sendToEmbed(), 500);
        return;
      }
      const options = { theme: 'light', direction: 'RIGHT' };
      const payload =
        this.jsonInput && this.isJsonValid
          ? this.jsonInput
          : JSON.stringify(EXAMPLE_TEMPLATE, null, 2);
      iframe.contentWindow.postMessage({ json: payload, options }, '*');
    },

    formatJson() {
      try {
        this.jsonInput = JSON.stringify(JSON.parse(this.jsonInput), null, 2);
        this.isJsonValid = true;
        this.sendToEmbed();
      } catch {
        /* invalid — leave as-is */
      }
    },

    clearJson() {
      this.jsonInput = '';
      this.isJsonValid = false;
      this.editingTemplate = null;
      this.sendToEmbed();
    },

    clearEditing() {
      this.editingTemplate = null;
      this.jsonInput = '';
      this.isJsonValid = false;
      this.sendToEmbed();
    },

    loadExample() {
      this.jsonInput = JSON.stringify(EXAMPLE_TEMPLATE, null, 2);
      this.validateJson(this.jsonInput);
      this.sendToEmbed();
    },

    // ── Save ──
    openSaveDialog() {
      this.saveForm.name = this.editingTemplate ? this.editingTemplate.name : '';
      this.saveForm.description = this.editingTemplate
        ? this.editingTemplate.description || ''
        : '';
      this.saveDialog = true;
    },

    async confirmSave() {
      if (!this.saveForm.name.trim()) return;
      this.saving = true;
      try {
        const dto = {
          name: this.saveForm.name.trim(),
          description: this.saveForm.description.trim(),
          content: this.jsonInput,
        };
        if (this.editingTemplate) {
          const res = await sacTemplateApi.update(this.editingTemplate.id, dto);
          this.editingTemplate = res.data;
        } else {
          const res = await sacTemplateApi.create(dto);
          this.editingTemplate = res.data;
        }
        this.saveDialog = false;
      } catch (err) {
        console.error('Error saving template:', err);
      } finally {
        this.saving = false;
      }
    },

    // ── Load / Manage ──
    async openLoadDialog() {
      this.loadDialog = true;
      await this.fetchTemplates();
    },

    async fetchTemplates() {
      this.loadingTemplates = true;
      try {
        const res = await sacTemplateApi.getAll();
        this.templates = res.data;
      } catch (err) {
        console.error('Error loading templates:', err);
      } finally {
        this.loadingTemplates = false;
      }
    },

    loadTemplateContent(tmpl) {
      try {
        this.jsonInput = JSON.stringify(JSON.parse(tmpl.content), null, 2);
      } catch {
        this.jsonInput = tmpl.content;
      }
      this.editingTemplate = tmpl;
      this.validateJson(this.jsonInput);
      this.sendToEmbed();
      this.loadDialog = false;
    },

    // ── Delete ──
    confirmDeleteTemplate(tmpl) {
      this.templateToDelete = tmpl;
      this.deleteDialog = true;
    },

    async executeDelete() {
      if (!this.templateToDelete) return;
      this.deleting = true;
      try {
        await sacTemplateApi.remove(this.templateToDelete.id);
        this.templates = this.templates.filter(t => t.id !== this.templateToDelete.id);
        if (this.editingTemplate && this.editingTemplate.id === this.templateToDelete.id) {
          this.clearEditing();
        }
        this.deleteDialog = false;
      } catch (err) {
        console.error('Error deleting template:', err);
      } finally {
        this.deleting = false;
        this.templateToDelete = null;
      }
    },

    formatDate(isoString) {
      if (!isoString) return '';
      try {
        return isoString.slice(0, 10);
      } catch {
        return isoString;
      }
    },
  },

  mounted() {
    this.sendToEmbed();
  },

  beforeDestroy() {
    clearTimeout(this.syncTimeout);
  },
};
</script>

<style scoped>
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 24px;
}

.st-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  min-height: 600px;
}

/* Header */
.st-page-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  border-radius: 14px 14px 0 0;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.st-header-icon-wrap {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Layout */
.st-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  background: #ffffff;
  border-radius: 0 0 14px 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* Panels */
.st-left-panel {
  display: flex;
  flex-direction: column;
  width: 420px;
  min-width: 320px;
  flex-shrink: 0;
  border-right: 1px solid #e8eaf6;
}
.st-right-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.st-panel-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 100%);
  padding: 10px 16px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Banners */
.st-editing-banner {
  background: #e3f2fd;
  border-bottom: 2px solid #90caf9;
  padding: 5px 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.st-error-banner {
  background: #fff3f3;
  border-bottom: 2px solid #ffcdd2;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.st-valid-banner {
  background: #f1f8f2;
  border-bottom: 2px solid #c8e6c9;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Editor */
.st-editor {
  flex: 1;
  width: 100%;
  min-height: 0;
  resize: none;
  border: none;
  outline: none;
  font-family: 'Fira Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 0.82rem;
  line-height: 1.6;
  padding: 14px 16px;
  background: #f8f9fc;
  color: #263238;
  box-sizing: border-box;
  overflow-y: auto;
}
.st-editor::placeholder {
  color: #b0bec5;
  font-style: italic;
}

/* Action Bar */
.st-action-bar {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-top: 1px solid #e8eaf6;
  background: #f5f7ff;
  flex-shrink: 0;
}
.st-btn {
  font-weight: 600 !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  text-transform: none !important;
}
.st-save-btn {
  color: #37474f !important;
}

/* Iframe */
.st-iframe-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.st-iframe {
  border: none;
  display: block;
  width: 100%;
  height: 100%;
}

/* Dialog */
.st-dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
}
.st-dialog-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 100%);
  padding: 16px 22px;
  display: flex;
  align-items: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
}

/* Template list */
.st-template-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}
.st-template-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid #e8eaf6;
  border-radius: 10px;
  transition: background 0.2s;
  gap: 12px;
}
.st-template-row:hover {
  background: #f5f7ff;
}
.st-template-info {
  flex: 1;
  min-width: 0;
}
.st-template-name {
  font-weight: 600;
  font-size: 1rem;
  color: #263238;
}
.st-template-desc {
  font-size: 0.88rem;
  color: #78909c;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.st-template-meta {
  font-size: 0.9rem;
  color: #78909c;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

@media (max-width: 900px) {
  .st-layout {
    flex-direction: column;
  }
  .st-left-panel {
    width: 100%;
    min-width: 0;
  }
  .st-editor {
    min-height: 220px;
    max-height: 340px;
  }
  .st-iframe-wrap {
    height: 360px;
    flex: none;
  }
}
</style>
